import React from 'react';
import { z } from 'zod/v3';
import { err, ok, type Result } from 'neverthrow';
import { v4 } from 'uuid';
import { ResourceType } from '@cotera/api';
import { toast } from '@cotera/client/app/components/ui';
import type {
  ApiErr,
  TenantedClient,
} from '@cotera/client/app/etc/api/typed-client';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import {
  resolveResourceNavigator,
  type ResourceNavigator,
} from '../resources/navigation';
import { emitLocalDatasetImport } from './dataset-import';
import { DatasetsListPageModel } from './datasets-list-page.model';

type Params = {
  file: File;
};

/** Only the modal-stack surface this action actually calls. */
type ModalManager = {
  push: (props: { view: () => React.ReactNode }) => void;
  close: () => void;
};

/** Only the client surface this action actually calls. */
type UploadCsvDatasetActionClient = {
  resource: {
    dataset: Pick<
      TenantedClient['resource']['dataset'],
      'createCsvPending' | 'uploadCsvBytes'
    >;
  };
};

const uploadErrorMessage = (error: ApiErr): string =>
  error.err.message ?? 'Failed to upload CSV dataset.';

export class UploadCsvDatasetAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Upload CSV dataset';
  description = 'Upload a CSV file as a dataset';
  inputSchema = z.object({
    file: z.custom<File>(
      (value) => typeof File !== 'undefined' && value instanceof File
    ),
  });
  group = 'Dataset';
  icon = 'database';

  constructor(
    private readonly client: UploadCsvDatasetActionClient,
    private readonly fallbackNavigator: ResourceNavigator,
    private readonly modalManager: ModalManager
  ) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    // The framework never runs inputSchema, so a palette/deep-link invocation
    // can reach here with nothing picked.
    const file: File | undefined = params.file;
    if (file === undefined) {
      toast.error('No CSV file selected.');
      return err([{ t: 'error', message: 'No CSV file selected' }]);
    }

    // The dataset is created before the file is sent, in a request that carries
    // nothing but its details and so answers immediately. Uploading the file to
    // create it would mean waiting out the whole transfer — minutes on a large
    // CSV — before there was anything to open.
    const datasetId = v4();
    const created = await this.client.resource.dataset.createCsvPending({
      id: datasetId,
      originalFilename: file.name,
      ...(file.type !== '' && { mimeType: file.type }),
      sizeBytes: file.size,
    });

    if (created.isErr()) {
      const msg = uploadErrorMessage(created.error);
      toast.error(msg);
      return err([{ t: 'error', message: msg }]);
    }

    // Page first, upload second: from here the file is going to a dataset that
    // already exists, so the progress belongs on its page — where anyone who
    // opens it sees the import, not just whoever started it. Seeded locally
    // because the bytes are in this browser and no server can see them move.
    emitLocalDatasetImport(datasetId, { phase: 'uploading', fraction: 0 });
    context.getInScopeModelOfType(DatasetsListPageModel)?.refresh();
    resolveResourceNavigator(context, this.fallbackNavigator).open({
      resourceType: ResourceType.Dataset,
      resourceId: datasetId,
    });

    // A connection that drops mid-transfer rejects rather than resolving to an
    // error, and it is the likeliest thing to go wrong on a long upload — the
    // one failure the dataset waiting on it must not miss.
    const uploaded: Result<unknown, ApiErr> = await this.client.resource.dataset
      .uploadCsvBytes({
        id: datasetId,
        file,
        onUploadProgress: (fraction) =>
          emitLocalDatasetImport(datasetId, { phase: 'uploading', fraction }),
      })
      .catch((cause: unknown) =>
        err({
          t: 'client' as const,
          err: {
            errorType: 'UploadFailed',
            message:
              cause instanceof Error
                ? cause.message
                : 'The upload did not finish.',
          },
        })
      );

    if (uploaded.isErr()) {
      const msg = uploadErrorMessage(uploaded.error);
      // The dataset is already on screen waiting on this file, so the failure
      // has to reach it. The server records the same thing against the dataset
      // for anyone else watching, and for this page after a reload.
      emitLocalDatasetImport(datasetId, { phase: 'failed', error: msg });
      toast.error(msg);
      return err([{ t: 'error', message: msg }]);
    }

    // The bytes are down and the saga owns it from here, so hand the page over
    // to the server's phases rather than leaving it on a finished upload bar.
    emitLocalDatasetImport(datasetId, { phase: 'staged' });
    return ok({ resourceId: datasetId });
  }
}
